/**
 * Sets up a scroller to scroll through child elements.
 *
 * Can support more than one on a page, and supports basic touch swiping (non-kinetic).
 *
 * Structure:
 * .jm-scroller (Viewport, position relative)
 *      .jm-scroller__outer (overflow hidden, position relative)
 *      .jm-scroller__inner (Get's scrolled, position absolute, white-space nowrap)
 *          .jm-scroller__inner__single (Element(s), display inline-block)
 *
 * All child elements should be the same width, otherwise you're gonna
 * have a bad time.
 */

import { enableElement, disableElement } from './element-enabler';

// crossStyle :: Element, String, String -> Element
const crossStyle = (element, attribute, value) => {
    const attributeU = attribute.charAt(0).toUpperCase() + attribute.slice(1);
    element.style[`webkit${attributeU}`] = value; // Target Webkit
    element.style[`Moz${attributeU}`] = value; // Target Firefox
    element.style[attribute] = value; // Default
    return element;
};

const swipeHandler = (parent, leftCallback, rightCallback) => {
    let x = -1;
    parent.addEventListener('touchstart', e => {
        x = e.touches && e.touches.length ? e.touches[0].pageX : -1;
    });
    parent.addEventListener('touchend', e => {
        const eX = x >= 0 && e.changedTouches && e.changedTouches.length ? e.changedTouches[0].pageX : -1;
        if (eX < 0) return;
        const dX = x - eX;
        if (dX > 20) rightCallback();
        else if (dX < -20) leftCallback();
    });
};

const setup = viewport => {
    // To transform
    const scroller = viewport.querySelector('.jm-scroller__inner');
    if (!scroller) return;

    // Only get the first element
    const elements = [].slice.call(scroller.querySelectorAll('.jm-scroller__inner__single'));
    if (!elements.length) return;

    const leftButton = viewport.querySelector('.jm-scroller__nav--left');
    const rightButton = viewport.querySelector('.jm-scroller__nav--right');

    const count = elements.length;
    let current = 0;
    let transform = 0;

    let vW = 0; // Viewport width
    let eW = 0; // Element width
    let eV = 0; // Visible elements
    let end = 0; // The last index we should go to

    // Measure the elements
    const measure = () => {
        vW = viewport.offsetWidth;
        eW = elements[0].offsetWidth;
        eV = Math.round(vW / eW);
        end = count - eV;
    };

    const clampCurrent = i => (i <= 0 ? 0 : i >= end ? end : i);

    const updateTransform = () => (transform = -current * eW);

    const updateScroll = () => crossStyle(scroller, 'transform', `translateX(${transform}px)`);

    const updateButtons = () => {
        // At the start
        if (current <= 0) {
            disableElement(leftButton);
            enableElement(rightButton);
        }
        // At the end
        else if (current >= end) {
            disableElement(rightButton);
            enableElement(leftButton);
        } else {
            enableElement(leftButton);
            enableElement(rightButton);
        }
    };

    const buttonHandler = i => () => {
        current = clampCurrent(current + i);
        updateTransform();
        updateScroll();
        updateButtons();
    };

    const buttonHandlerZero = buttonHandler(0);
    const buttonHandlerLeft = buttonHandler(-1);
    const buttonHandlerRight = buttonHandler(1);

    if (leftButton) leftButton.addEventListener('click', buttonHandlerLeft);
    if (rightButton) rightButton.addEventListener('click', buttonHandlerRight);

    // Re-measure on resize.
    window.addEventListener('resize', () => {
        measure();
        buttonHandlerZero();
    });

    // Touch
    swipeHandler(viewport, buttonHandlerLeft, buttonHandlerRight);

    // Run once for good 'measure' (get it?)
    measure();
    buttonHandlerZero();
};

// init :: String -> [Element]|[]
const init = (selector = '[data-js="jmScroller"]') => {
    const scrollers = [].slice.call(document.querySelectorAll(selector));
    scrollers.map(setup);
};

export { init };
export default { init };
